import type { Fetcher } from "../fetcher";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";
import type {
  Order,
  OrderDetail,
  OrderExpense,
  OrderStats,
  Trip,
  OrderCargoTrip,
} from "../entities/order";
import type { OrderCargo } from "../entities/cargo";
import type {
  OrderFilters,
  CreateOrderDto,
  UpdateOrderDto,
  ChangeOrderStatusDto,
  CreateOrderCargoDto,
  UpdateOrderCargoDto,
  CreateOrderTripDto,
  UpdateOrderTripDto,
  CreateOrderExpenseDto,
  UpdateOrderExpenseDto,
  SetFinancialStatusOverrideDto,
  ConfirmQuotePayload,
  ConfirmQuoteResponse,
  OrderCargoQueryFilters,
  OrderEventDTO,
  ListOrderEventsParams,
  OrderCommentDTO,
  ListOrderCommentsParams,
  CreateOrderCommentDto,
  UpdateOrderCommentDto,
} from "./types";

export function createOrdersRepository(fetcher: Fetcher) {
  return {
    async list(filters: OrderFilters = {}): Promise<{
      items: Order[];
      pagination: { total: number; page?: number; totalPages?: number };
      stats?: OrderStats;
    }> {
      const params = new URLSearchParams();
      if (filters.page) params.append("page", String(filters.page));
      if (filters.limit) params.append("limit", String(filters.limit));
      if (filters.search) params.append("search", filters.search);
      if (filters.scope) params.append("scope", filters.scope);
      if (filters.clientId) params.append("clientId", filters.clientId);
      if (filters.managerId) params.append("managerId", filters.managerId);
      if (filters.sellerId) params.append("sellerId", filters.sellerId);
      if (filters.forwarderId) params.append("forwarderId", filters.forwarderId);
      if (filters.currencyId) params.append("currencyId", filters.currencyId);
      if (filters.status) params.append("status", filters.status);
      if (filters.orderDateFrom) params.append("orderDateFrom", filters.orderDateFrom);
      if (filters.orderDateTo) params.append("orderDateTo", filters.orderDateTo);
      if (filters.clientContactId) params.append("clientContactId", filters.clientContactId);
      if (filters.archived) params.append("archived", filters.archived);
      if (filters.status) params.append("status", filters.status);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      const query = params.toString();
      const url = query ? `/orders?${query}` : "/orders";

      const response = await fetcher<ApiCollectionResponse<Order, { stats?: OrderStats }>>(url);
      const total = response.data?.pagination?.total ?? 0;
      const limit = response.data?.pagination?.limit || 25;
      return {
        items: response.data?.items ?? [],
        pagination: {
          total,
          page: response.data?.pagination?.page ?? 1,
          totalPages: Math.ceil(total / limit) || 1,
        },
        stats: response.data?.stats,
      };
    },

    async get(id: string): Promise<OrderDetail> {
      const response = await fetcher<ApiSingleResponse<OrderDetail>>(`/orders/${id}`);
      return response.data;
    },

    async create(payload: CreateOrderDto): Promise<Order> {
      const response = await fetcher<ApiSingleResponse<Order>>("/orders", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async update(id: string, payload: UpdateOrderDto): Promise<Order> {
      const response = await fetcher<ApiSingleResponse<Order>>(`/orders/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async delete(id: string): Promise<void> {
      await fetcher(`/orders/${id}`, { method: "DELETE" });
    },

    async changeStatus(id: string, payload: ChangeOrderStatusDto): Promise<Order> {
      const response = await fetcher<ApiSingleResponse<Order>>(`/orders/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },

    async archive(id: string): Promise<void> {
      await fetcher(`/orders/${id}/archive`, { method: "POST" });
    },

    async unarchive(id: string): Promise<void> {
      await fetcher(`/orders/${id}/unarchive`, { method: "POST" });
    },

    async duplicate(id: string): Promise<Order> {
      const response = await fetcher<ApiSingleResponse<Order>>(
        `/orders/${id}/duplicate`,
        { method: "POST" },
      );
      return response.data;
    },

    // ─── Cargo ────────────────────────────────────────────────────────────

    async listCargos(
      orderId: string,
      filters: OrderCargoQueryFilters = {},
    ): Promise<{ items: OrderCargo[]; total: number }> {
      const url = filters.filter
        ? `/orders/${orderId}/cargos?filter=${filters.filter}`
        : `/orders/${orderId}/cargos`;
      const response = await fetcher<ApiCollectionResponse<OrderCargo>>(url);
      return {
        items: response.data?.items ?? [],
        total: response.data?.pagination?.total ?? 0,
      };
    },

    async getCargo(orderId: string, cargoId: string): Promise<OrderCargo> {
      const response = await fetcher<ApiSingleResponse<OrderCargo>>(
        `/orders/${orderId}/cargos/${cargoId}`,
      );
      return response.data;
    },

    async createCargo(
      orderId: string,
      payload: CreateOrderCargoDto,
    ): Promise<OrderCargo> {
      const response = await fetcher<ApiSingleResponse<OrderCargo>>(
        `/orders/${orderId}/cargos`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async updateCargo(
      orderId: string,
      cargoId: string,
      payload: UpdateOrderCargoDto,
    ): Promise<{ message: string }> {
      const response = await fetcher<ApiSingleResponse<{ message: string }>>(
        `/orders/${orderId}/cargos/${cargoId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return { message: response.data?.message ?? "Cargo updated" };
    },

    async deleteCargo(orderId: string, cargoId: string): Promise<void> {
      await fetcher(`/orders/${orderId}/cargos/${cargoId}`, { method: "DELETE" });
    },

    // ─── Trips ────────────────────────────────────────────────────────────

    async listTrips(orderId: string): Promise<{ items: Trip[]; total: number }> {
      const response = await fetcher<ApiCollectionResponse<Trip>>(
        `/orders/${orderId}/trips`,
      );
      return {
        items: response.data?.items ?? [],
        total: response.data?.pagination?.total ?? 0,
      };
    },

    async createTrip(orderId: string, payload: CreateOrderTripDto): Promise<Trip> {
      const response = await fetcher<ApiSingleResponse<Trip>>(
        `/orders/${orderId}/trips`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async updateTrip(
      orderId: string,
      tripId: string,
      payload: UpdateOrderTripDto,
    ): Promise<Trip> {
      const response = await fetcher<ApiSingleResponse<Trip>>(
        `/orders/${orderId}/trips/${tripId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async deleteTrip(orderId: string, tripId: string): Promise<void> {
      await fetcher(`/orders/${orderId}/trips/${tripId}`, { method: "DELETE" });
    },

    // ─── Financial status override ─────────────────────────────────────────

    async setFinancialStatusOverride(
      orderId: string,
      payload: SetFinancialStatusOverrideDto,
    ): Promise<OrderDetail> {
      const response = await fetcher<ApiSingleResponse<OrderDetail>>(
        `/orders/${orderId}/financial-status-override`,
        { method: "PUT", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async clearFinancialStatusOverride(orderId: string): Promise<OrderDetail> {
      const response = await fetcher<ApiSingleResponse<OrderDetail>>(
        `/orders/${orderId}/financial-status-override`,
        { method: "DELETE" },
      );
      return response.data;
    },

    // ─── Expenses ─────────────────────────────────────────────────────────
    //
    // No list method: the lines ride on the order detail payload
    // (`OrderDetail.expenses`), so these three mutate and the caller
    // invalidates the order detail.

    async createExpense(
      orderId: string,
      payload: CreateOrderExpenseDto,
    ): Promise<OrderExpense> {
      const response = await fetcher<ApiSingleResponse<OrderExpense>>(
        `/orders/${orderId}/expenses`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async updateExpense(
      orderId: string,
      expenseId: string,
      payload: UpdateOrderExpenseDto,
    ): Promise<OrderExpense> {
      const response = await fetcher<ApiSingleResponse<OrderExpense>>(
        `/orders/${orderId}/expenses/${expenseId}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    async deleteExpense(orderId: string, expenseId: string): Promise<void> {
      await fetcher(`/orders/${orderId}/expenses/${expenseId}`, {
        method: "DELETE",
      });
    },

    // ─── Cargo trips ──────────────────────────────────────────────────────

    async listCargoTrips(orderId: string): Promise<OrderCargoTrip[]> {
      const response = await fetcher<ApiCollectionResponse<OrderCargoTrip>>(
        `/orders/${orderId}/cargo-trips`,
      );
      return response.data?.items ?? [];
    },

    // ─── Quote Confirmation ───────────────────────────────────────────────

    async createFromQuote(
      quoteId: string,
      payload: ConfirmQuotePayload,
    ): Promise<ConfirmQuoteResponse> {
      const response = await fetcher<ApiSingleResponse<ConfirmQuoteResponse>>(
        `/quotes/${quoteId}/confirm-and-create-order`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return response.data;
    },

    // ─── Activity / Audit Events ──────────────────────────────────────────

    async listEvents(
      orderId: string,
      params: ListOrderEventsParams = {},
    ): Promise<{
      items: OrderEventDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }> {
      const res = await fetcher<ApiCollectionResponse<OrderEventDTO>>(
        `/orders/${orderId}/events`,
        { params: params as Record<string, unknown> },
      );
      const payload =
        res.data ??
        ({} as Partial<{
          items: OrderEventDTO[];
          pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
          };
        }>);
      const pagination = payload.pagination ?? {};
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
      };
    },

    // ─── Comments ─────────────────────────────────────────────────────────

    async listComments(
      orderId: string,
      params: ListOrderCommentsParams = {},
    ): Promise<{
      items: OrderCommentDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }> {
      const res = await fetcher<ApiCollectionResponse<OrderCommentDTO>>(
        `/orders/${orderId}/comments`,
        { params: params as Record<string, unknown> },
      );
      const payload =
        res.data ??
        ({} as Partial<{
          items: OrderCommentDTO[];
          pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
          };
        }>);
      const pagination = payload.pagination ?? {};
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
      };
    },

    async createComment(
      orderId: string,
      data: CreateOrderCommentDto,
    ): Promise<OrderCommentDTO> {
      const res = await fetcher<ApiSingleResponse<OrderCommentDTO>>(
        `/orders/${orderId}/comments`,
        { method: "POST", body: JSON.stringify(data) },
      );
      return res.data;
    },

    async updateComment(
      orderId: string,
      commentId: string,
      data: UpdateOrderCommentDto,
    ): Promise<OrderCommentDTO> {
      const res = await fetcher<ApiSingleResponse<OrderCommentDTO>>(
        `/orders/${orderId}/comments/${commentId}`,
        { method: "PATCH", body: JSON.stringify(data) },
      );
      return res.data;
    },

    async deleteComment(orderId: string, commentId: string): Promise<void> {
      await fetcher(`/orders/${orderId}/comments/${commentId}`, {
        method: "DELETE",
      });
    },

  };
}
